import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import { 
  Users, 
  Search, 
  Plus,
  MapPin
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('利用者データの取得に失敗しました', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter(client => 
    (client.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (client.name_kana || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (client.recipient_number || '').includes(searchQuery)
  );

  const getServiceTypeBadge = (type) => {
    const colors = {
      '日中一時支援': 'badge-success',
      '移動支援': 'badge-warning',
      '地域生活支援': 'badge-neutral'
    };
    return colors[type] || 'badge-neutral';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="clients-page">
      {/* ヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            利用者管理
          </h1>
          <p className="text-muted-foreground mt-1">
            登録済み利用者: {clients.length}名
          </p>
        </div>
        <Link to="/clients/new">
          <Button className="btn-active" data-testid="add-client-button">
            <Plus size={18} className="mr-2" />
            新規登録
          </Button>
        </Link>
      </div>

      {/* 検索 */}
      <Card className="card-surface">
        <CardContent className="pt-6">
          <div className="relative">
            <Search 
              size={18} 
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" 
            />
            <Input
              placeholder="氏名・カナ・受給者番号で検索..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 max-w-md"
              data-testid="client-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* 利用者一覧 */}
      {filteredClients.length === 0 ? (
        <Card className="card-surface">
          <CardContent className="py-12 text-center">
            <Users size={48} className="mx-auto mb-4 text-muted-foreground/50" />
            <p className="text-muted-foreground">
              {searchQuery ? '検索条件に一致する利用者が見つかりません' : '利用者が登録されていません'}
            </p>
            {!searchQuery && (
              <Link to="/clients/new">
                <Button className="mt-4" variant="outline">
                  最初の利用者を登録する
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="card-surface overflow-hidden">
          <div className="table-container">
            <Table>
              <TableHeader className="table-header">
                <TableRow>
                  <TableHead className="table-th">氏名</TableHead>
                  <TableHead className="table-th">受給者番号</TableHead>
                  <TableHead className="table-th">サービス</TableHead>
                  <TableHead className="table-th">住所</TableHead>
                  <TableHead className="table-th">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClients.map((client) => (
                  <TableRow 
                    key={client.id} 
                    className="table-row-hover"
                    data-testid={`client-row-${client.id}`}
                  >
                    <TableCell className="table-td">
                      <div>
                        <p className="font-medium">{client.name}</p>
                        <p className="text-xs text-muted-foreground">{client.name_kana}</p>
                      </div>
                    </TableCell>
                    <TableCell className="table-td font-mono text-sm">
                      {client.recipient_number}
                    </TableCell>
                    <TableCell className="table-td">
                      <div className="flex flex-wrap gap-1">
                        {(client.services || []).map((svc, idx) => (
                          <span key={idx} className={getServiceTypeBadge(typeof svc === 'string' ? svc : svc.service_type)}>
                            {typeof svc === 'string' ? svc : svc.service_type}
                          </span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="table-td">
                      <div className="space-y-1">
                        {client.address && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <MapPin size={14} />
                            {client.address.substring(0, 20)}{client.address.length > 20 ? '...' : ''}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="table-td">
                      <div className="flex gap-2">
                        <Link to={`/clients/${client.id}`}>
                          <Button 
                            variant="outline" 
                            size="sm"
                            data-testid={`view-client-${client.id}`}
                          >
                            詳細
                          </Button>
                        </Link>
                        <Link to={`/clients/${client.id}/edit`}>
                          <Button 
                            variant="outline" 
                            size="sm"
                            data-testid={`edit-client-${client.id}`}
                          >
                            編集
                          </Button>
                        </Link>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
